import { computed, Injectable, linkedSignal, signal } from '@angular/core';

import {
  DEFAULT_DATE_FORMAT,
  DEFAULT_LOCALE,
  DEFAULT_TIME_ZONE,
  isSupportedDateFormat,
  isSupportedLocale,
} from '@tfs/date-format';

import { CustomerSubscription, Profile, SubscriptionStatus } from '../models/auth.models';
import { Flag, flags } from '../models/flag.constants';
import { roles } from '../models/role.constants';

@Injectable({
  providedIn: 'root',
})
export class ProfileService {
  private readonly _userProfile = signal<Profile | null>(null);

  readonly userProfile = this._userProfile.asReadonly();

  readonly isAdmin = computed(() => this.userProfile()?.roles?.includes('administrator') ?? false);

  readonly isOwner = computed(() => this.userProfile()?.roles?.includes(roles.Owner) ?? false);

  readonly hasCribwiseIntegration = computed(() => {
    const profile = this.userProfile();
    return profile?.features?.includes('integrationsProvider:Cribwise') ?? false;
  });

  /** Mirrors the backend's `integrationsProvider:Manual` feature flag, which is granted
   * unconditionally to every customer today (no configuration step exists yet — see
   * `stockIntegration`). Kept as its own signal so `stockIntegration` can start counting it once a
   * real "configured Manual" signal lands on the backend, without consumers changing. */
  readonly hasManualIntegration = computed(() => {
    const profile = this.userProfile();
    return profile?.features?.includes('integrationsProvider:Manual') ?? false;
  });

  /** Whether the customer has a stock provider actually configured — the single gate consumers
   * should check before showing stock UI, instead of branching on a specific provider. Cribwise-only
   * for now: `ManualUserProfileFeaturesContributor` (server/Modules/InventoryManagementModule/.../
   * ManualInventory/UserProfile/ManualUserProfileFeaturesContributor.cs) returns the same static
   * feature array for every `customerId`/`userId` with no conditional at all, so `hasManualIntegration`
   * is unconditionally true for every customer — OR-ing it in here would make this always true.
   * Revisit once Manual has a real configured-state signal on the backend. */
  readonly stockIntegration = computed(() => this.hasCribwiseIntegration());

  readonly hasCamSystemsSelected = computed(() => {
    const profile = this.userProfile();
    return (profile?.camSoftwares?.length ?? 0) > 0;
  });

  readonly measurementSystem = computed(() => {
    return this.userProfile()?.measurementSystem || 'Metric';
  });

  readonly locale = computed<string>(() => {
    const value = this.userProfile()?.locale;
    return isSupportedLocale(value) ? value : DEFAULT_LOCALE;
  });

  readonly dateFormat = computed<string>(() => {
    const value = this.userProfile()?.dateFormat;
    return isSupportedDateFormat(value) ? value : DEFAULT_DATE_FORMAT;
  });

  readonly timeZone = computed<string>(() => {
    return this.userProfile()?.timeZone || DEFAULT_TIME_ZONE;
  });

  private readonly _subscription = signal<CustomerSubscription | null>(null);

  readonly subscription = this._subscription.asReadonly();

  readonly isSubscriptionInactive = computed(() => {
    const subscription = this.subscription();
    if (!subscription) return false;

    return (subscription.status as SubscriptionStatus) === 'Inactive';
  });

  /** The feature flags this account was granted, seeded from every profile the backend hands us and
   * writable from the developer page so a dev can try a feature out. Nothing is persisted: the next
   * profile the backend delivers re-seeds this, so a reload always returns to the backend's answer.
   * The flags themselves are configured in `features/feature-flags.<env>.json`. */
  private readonly _featureFlags = linkedSignal<Record<string, boolean>>(
    () => this.userProfile()?.flags ?? {}
  );

  readonly featureFlags = this._featureFlags.asReadonly();

  readonly camImportEnabled = computed(
    () => this._featureFlags()[flags.CamDatabaseImport] === true
  );
  readonly workOrderManagementEnabled = computed(
    () => this._featureFlags()[flags.WorkOrderModule] === true
  );
  readonly testAccountEndpointsEnabled = computed(
    () => this._featureFlags()[flags.TestAccountEndpoints] === true
  );
  readonly hasActiveCampaign = computed(
    () => this._featureFlags()[flags.NpsCampaignActive] === true
  );
  readonly acceptLanguageHeaderEnabled = computed(
    () => this._featureFlags()[flags.AcceptLanguageHeader] === true
  );
  /** 2D drawings render through the mesh viewer instead of the rasterised image — item/assembly
   * search, details, manual creation and the assembly builder. */
  readonly new2DViewerItemAssemblyEnabled = computed(
    () => this._featureFlags()[flags.New2DViewerItemAssembly] === true
  );
  /** Same switch for the catalog (external item) search and details. */
  readonly new2DViewerCatalogEnabled = computed(
    () => this._featureFlags()[flags.New2DViewerCatalog] === true
  );
  readonly assemblyBuilderV2Enabled = computed(
    () => this._featureFlags()[flags.AssemblyBuilderV2] === true
  );

  /** Shows the Cutting data tab on the assembly detail view, and with it the cutting data
   * calculator dialog opened from that tab. When off, the tab is not rendered and no cutting data
   * request is made. */
  readonly cuttingDataEnabled = computed(() => this._featureFlags()[flags.CuttingData] === true);

  /** Flips a feature flag for this session, from the developer page. */
  setFlag(flag: Flag, enabled: boolean): void {
    this._featureFlags.update(current => ({ ...current, [flag]: enabled }));
  }

  setUserProfile(profile: Profile | null): void {
    this._userProfile.set(profile);
  }

  setSubscription(subscription: CustomerSubscription | null): void {
    this._subscription.set(subscription);
  }
}
